import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import SimpleInput from './SimpleInput'

export default function TypeWriterInput({
  fullText,
  charDelay = 80,
  onComplete,
  typing = true,
  ...muiProps
}) {
  const [displayText, setDisplayText] = useState('')
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isUserEditing, setIsUserEditing] = useState(false)
  const typingCompletedRef = useRef(false)

  // Reset when fullText changes
  useEffect(() => {
    setDisplayText('')
    setCurrentIndex(0)
    setIsUserEditing(false)
    typingCompletedRef.current = false
  }, [fullText])

  // Handle immediate display when typing is false
  useEffect(() => {
    if (!typing && !typingCompletedRef.current) {
      setDisplayText(fullText)
      typingCompletedRef.current = true
      if (onComplete) {
        onComplete(fullText, false)
      }
    }
  }, [typing, fullText, onComplete])

  // Handle typewriter effect
  useEffect(() => {
    // Skip if not typing or already completed or user is editing
    if (!typing || isUserEditing || typingCompletedRef.current) {
      return
    }

    if (currentIndex < fullText.length) {
      const timeoutId = setTimeout(() => {
        setDisplayText(fullText.substring(0, currentIndex + 1))
        setCurrentIndex((prevIndex) => prevIndex + 1)
      }, charDelay)

      return () => clearTimeout(timeoutId)
    }
    // When typing is complete
    else if (currentIndex === fullText.length && !typingCompletedRef.current) {
      typingCompletedRef.current = true
      if (onComplete) {
        onComplete(fullText, false)
      }
    }
  }, [currentIndex, fullText, charDelay, isUserEditing, typing, onComplete])

  const handleChange = (e) => {
    const newValue = e.target.value
    setDisplayText(newValue)
    setIsUserEditing(true)

    // Always call onComplete for user edits
    if (onComplete) {
      onComplete(newValue, false)
    }
  }

  return (
    <SimpleInput
      {...muiProps}
      value={displayText}
      fullWidth
      onChange={handleChange}
      onClick={() => setIsUserEditing(true)}
    />
  )
}

TypeWriterInput.propTypes = {
  fullText: PropTypes.string.isRequired,
  charDelay: PropTypes.number,
  onComplete: PropTypes.func,
  typing: PropTypes.bool,
}
